import * as globals from '@wandb/weave/common/css/globals.styles';
import {isArray, isObject} from 'lodash';
import React, {type FC, type ReactNode, useMemo} from 'react';
import {
  JSONTree as ReactJSONTree,
  type KeyPath,
  type ValueRenderer,
} from 'react-json-tree';

import {useViewer} from '../../state/viewer/hooks';
import {type Bar} from '../../util/plotHelpers/types';
import {isUnpackedHistogramNode, type UnpackedHistogram} from '../../util/runs';
import {useHideSummaryHistogramsRampFlag} from '../../util/useRampFlag';
import BarChart from '../vis/BarChart/BarChart';
import {CopyableText} from './CopyableText';
import {useFilteredJSON} from './FilteredJSONProvider';
import {type FilterJSONOptions} from './util/filterJSON';
import {type JSONNode} from './util/jsonTypes';
import {wbJSONTreeTheme} from './util/wbJSONTreeTheme';

export const FilteredJSONTree = ({
  json,
  filterOptions,
  rootKeyLabel,
}: {
  json?: JSONNode;
  filterOptions?: FilterJSONOptions;
  rootKeyLabel: string;
}) => {
  const filteredJSON = useFilteredJSON({
    json,
    filterOptions,
  });

  // Keep a stable keyPath to avoid re-rendering the tree nodes
  const rootKeyPath = useMemo(() => [rootKeyLabel], [rootKeyLabel]);

  const valueRenderer: ValueRenderer = (valueAsString, value) => {
    // TODO: move histogram display logic out of here to keep the filtered tree component more general
    //  i.e. (remove the dependency on runs)
    if (isUnpackedHistogramNode(value)) {
      return <TreeValueHistogram histogram={value} />;
    }
    return (
      <CopyableText
        rootLocation={rootKeyLabel}
        copyText={valueAsString as string}>
        {value as ReactNode}
      </CopyableText>
    );
  };

  // TODO(perf): this re-renders the whole tree with each search. stop doing that if perf is an issue.
  return (
    <div className="FilteredJSONTree">
      <ReactJSONTree
        data={filteredJSON}
        keyPath={rootKeyPath}
        theme={wbJSONTreeTheme}
        shouldExpandNodeInitially={shouldExpandNode}
        sortObjectKeys={sortObjectKeys as (a: unknown, b: unknown) => number} // TODO: type this better
        // TODO: move histogram display logic out of here to keep the filtered tree component more general
        //  i.e. (remove the dependency on runs)
        isCustomNode={isUnpackedHistogramNode}
        valueRenderer={valueRenderer}
        // invertTheme={true}
        // postprocessValue={}
        // collectionLimit={}
        // getItemString={}
        // hideRoot={}
        // labelRenderer={([key]) => <strong>{key}</strong>}
        // sortObjectKeys={}
        // valueRenderer={}
      />
    </div>
  );
};

// TODO: move histogram display logic out of here to keep the filtered tree component more general
//  i.e. (remove the dependency on runs)
const TreeValueHistogram: FC<{histogram: UnpackedHistogram}> = ({
  histogram: {bins: histoBins, values: histoValues},
}) => {
  const dataPoints: Bar[] = useMemo(() => {
    const histogramValues = histoValues ?? [];
    const histogramBins = histoBins ?? [];
    return histogramValues.map((elem: number, index: number) => {
      const key =
        histogramBins.length > index
          ? histogramBins[index].toPrecision(4)
          : '-';
      return {
        key,
        value: elem,
        color: globals.TEAL_500,
        title: 'x= ' + key + ', y=',
      };
    });
  }, [histoBins, histoValues]);

  const viewer = useViewer();
  const hideHistogramsEnabled = useHideSummaryHistogramsRampFlag(
    viewer?.entity
  );

  if (!viewer || hideHistogramsEnabled.loading || hideHistogramsEnabled.data) {
    return (
      <div className="text-slate-400 inline italic opacity-50">histogram</div>
    );
  }

  return <BarChart bars={dataPoints} vertical={true} height={150}></BarChart>;
};

// https://stackoverflow.com/questions/2802341/natural-sort-of-alphanumerical-strings-in-javascript
const sortObjectKeys = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
}).compare;

// Auto-expand nodes with fewer than this many children
const EXPAND_CHILDREN_THRESHOLD = 5;
const shouldExpandNode = (keyPath: KeyPath, value: unknown, level: number) => {
  // console.log({keyPath, value, level});

  // Open the root node by default
  if (level === 0) {
    return true;
  }
  const childrenCount =
    (isObject(value) && Object.keys(value).length) ||
    (isArray(value) && value.length) ||
    0;

  return childrenCount < EXPAND_CHILDREN_THRESHOLD;
};
